import { useCallback, useState } from 'react';

export interface AccordionController {
    openItems: string[];
    toggleItem: (id: string) => void;
    openItem: (id: string) => void;
    closeItem: (id: string) => void;
    isItemOpen: (id: string) => boolean;
    allowMultipleOpen: boolean;
}

export interface UseAccordionControllerOptions {
    allowMultipleOpen?: boolean;
    initialOpenItems?: string[];
}

export function useAccordionController(
    allowMultipleOpenOrOptions: boolean | UseAccordionControllerOptions = false
): AccordionController {
    const options: UseAccordionControllerOptions =
        typeof allowMultipleOpenOrOptions === 'boolean'
            ? { allowMultipleOpen: allowMultipleOpenOrOptions }
            : allowMultipleOpenOrOptions;

    const { allowMultipleOpen = false, initialOpenItems = [] } = options;

    const [openItems, setOpenItems] = useState<string[]>(initialOpenItems);

    const toggleItem = useCallback(
        (id: string) => {
            setOpenItems(prev => {
                if (prev.includes(id)) {
                    return prev.filter(item => item !== id);
                } else {
                    return allowMultipleOpen ? [...prev, id] : [id];
                }
            });
        },
        [allowMultipleOpen]
    );

    const openItem = useCallback(
        (id: string) => {
            setOpenItems(prev => {
                if (prev.includes(id)) return prev;
                return allowMultipleOpen ? [...prev, id] : [id];
            });
        },
        [allowMultipleOpen]
    );

    const closeItem = useCallback((id: string) => {
        setOpenItems(prev => prev.filter(item => item !== id));
    }, []);

    const isItemOpen = useCallback((id: string) => openItems.includes(id), [openItems]);

    return {
        openItems,
        toggleItem,
        openItem,
        closeItem,
        isItemOpen,
        allowMultipleOpen,
    };
}
